import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FormMessage } from "@/components/ui/form-message";
import { adminBanDurations, type AdminBanDuration } from "@/features/admin/admin.types";
import { validateBanReason } from "@/features/admin/adminValidation";

type BanUserDialogProps = {
  busy?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: { duration: AdminBanDuration; reason: string }) => void;
  open: boolean;
};

export function BanUserDialog({ busy = false, onOpenChange, onSubmit, open }: BanUserDialogProps) {
  const { t } = useTranslation();
  const [duration, setDuration] = useState<AdminBanDuration>("7_days");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDuration("7_days");
      setReason("");
      setError(null);
    }
  }, [open]);

  function handleSubmit() {
    const validationError = validateBanReason(reason);
    setError(validationError);

    if (validationError) {
      return;
    }

    onSubmit({ duration, reason: reason.trim() });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("adminWorkspace.dialogs.banUser.title")}</DialogTitle>
          <DialogDescription>{t("adminWorkspace.dialogs.banUser.description")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <select
            className="w-full rounded-3xl border border-ink/10 bg-white/80 px-4 py-3 text-sm text-ink shadow-sm outline-none transition focus:border-clay/50 focus:ring-2 focus:ring-clay/20"
            value={duration}
            onChange={(event) => setDuration(event.target.value as AdminBanDuration)}
          >
            {adminBanDurations.map((item) => (
              <option key={item} value={item}>
                {t(`adminWorkspace.banDurations.${item}`)}
              </option>
            ))}
          </select>
          <textarea
            className="min-h-28 w-full rounded-[1.75rem] border border-ink/10 bg-white/80 px-4 py-3 text-sm text-ink shadow-sm outline-none transition focus:border-clay/50 focus:ring-2 focus:ring-clay/20"
            placeholder={t("adminWorkspace.dialogs.shared.reasonPlaceholder")}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          {error ? <FormMessage>{t(error)}</FormMessage> : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={busy}
            type="button"
            variant="destructive"
            onClick={handleSubmit}
          >
            {busy ? t("common.saving") : t("adminWorkspace.dialogs.banUser.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
